import {
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AnimatePresence } from 'framer-motion';

import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { FormError } from '@components/form-error';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconSms } from '@components/icons/phone-sms';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { InfoBox } from '@components/info-box';
import { Menu } from '@components/menu';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  DEFAULT_LINK_LABEL,
  extractName,
  FieldMappingEditorValue,
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldMappingVariables,
  FORM_LINK_FIELD,
  FormLinkDraft,
  getPlaceholderHtml,
  getVariableLabel,
} from '@components/text-editors/addons/field-mapping';
import { EditorMethods, EditorValue } from '@components/text-editors/text-editor';
import { useEvent } from '@hooks/use-event';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useEmailAddressOptions } from 'api/company/hooks';
import { createJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useWebSocket } from 'api/hooks';
import { useJourneyActionFormByJourneyActionId } from 'api/journey-action-forms/hooks';
import { JourneyActionForm } from 'api/journey-action-forms/requests';
import {
  useInvalidateJourneyActions,
  useJourneyAction,
  useUpdateJourneyAction,
} from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import {
  GROUP_ACTION_TYPES,
  JourneyActionDetailed,
  MappableVariable,
  VariableValue,
} from 'api/types';
import { ACTION_TYPES, getActionIconColor } from 'ui/actions/action-config';
import { TimeSavedContainer } from 'ui/helpers';
import { useFeature } from 'ui/hooks';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionRemoveButton,
  ActionSidebarLoading,
  createActionForm,
  DescriptionFormField,
  SidebarProps,
  useCreateActionFormStore,
  useVariableGroups,
  useVariableLocale,
} from 'ui/journey-builder/node-types/common';
import { EditRemindersContent } from 'ui/reminders/edit-reminders';
import { isInTemplateRoute } from 'ui/router';

import { useEditJourneyActionFormOverlay } from './edit-journey-action-form-overlay';
import { EmailPreviewPanel } from './email-preview-panel';
import { TemplateCardForm } from './form-card';
import { applyFormLinkDraft } from './form-link-draft';

import { SMSMessageCountWarning, TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';
import { previewEmailLocale, PreviewEmailProps } from '../send-email/email-preview';
import { getPreviewAttachments } from '../send-email/email-preview-modal';
import { PreviewSmsMenuItemView } from '../send-sms/preview-sms-menu-item';
import { PreviewSmsProps } from '../send-sms/preview-sms-modal';
import {
  collectBrokenVariableLabels,
  recipientTokens,
  replaceVariableTokensWithLabels,
} from '../shared/preview-variables';
import { EmailAddressOptions, SendFromEmailSelect } from '../shared/send-from-email-select';

type FormData = {
  description: string;
  executionMethod: TriggerMethod;
  assignees: SelectOption[];
  emailNotify: boolean;
  sendFromEmailId?: string;
  emailRecipient?: VariableValue[];
  emailSubject: FieldMappingEditorValue;
  emailMessage?: string;
  attachments?: FieldMappingEditorValue[];
  smsNotify: boolean;
  smsRecipient?: VariableValue[];
  smsMessage: EditorValue<{ variables?: FieldMappingVariables }>;
  emailReminderSwitch: boolean;
  smsReminderSwitch: boolean;
  hour: string;
  minute: string;
  frequency: number;
  amount: number;
  allowedWeekdays: number[];
} & TimeSavedData;

const ACTION_TYPE = ACTION_TYPES.combo_send_form;
const Form = createActionForm<FormData>();

const Header = ({ children, ...props }: PropsWithChildren<SidebarProps>) => (
  <ActionHeader actionType={ACTION_TYPE} {...props}>
    {children}
  </ActionHeader>
);

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId, isNewAction } = props;
  const { t } = useScopedTranslate(locale);
  const emailAddressOptions = useEmailAddressOptions();
  const {
    data,
    isLoading: isJourneyActionLoading,
    isError,
  } = useJourneyAction<'combo_send_form'>(journeyActionId);

  const { data: journeyActionFormData, isLoading: isJourneyActionFormLoading } =
    useJourneyActionFormByJourneyActionId(journeyActionId);

  const formShortId = journeyActionFormData?.shortId ?? '';

  const isLoading =
    isJourneyActionLoading || isJourneyActionFormLoading || emailAddressOptions.isLoading;

  const showEditJourneyActionFormOverlay = useEditJourneyActionFormOverlay();
  const { invalidateJourneyAction } = useInvalidateJourneyActions();

  useEffect(() => {
    if (isNewAction && formShortId) {
      showEditJourneyActionFormOverlay({
        formId: formShortId,
        onClose: () => {
          invalidateJourneyAction(journeyActionId);
        },
        actionVariables: data?.actionVariables ?? [],
      });
    }
  }, [
    isNewAction,
    formShortId,
    journeyActionId,
    showEditJourneyActionFormOverlay,
    invalidateJourneyAction,
    isLoading,
    data?.actionVariables,
  ]);

  if (isLoading) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  if (isError || !data || !journeyActionFormData) {
    return (
      <Sidebar>
        <Header {...props} />
        <Sidebar.Content>
          <Stack space={4}>
            <InfoBox
              header={t('deprecatedHeader')}
              variant="orange"
              icon={<IconFormIndicatorWarning />}
              text={t('deprecatedAlert')}
            />
          </Stack>
        </Sidebar.Content>
        <Sidebar.Footer>
          <ActionRemoveButton action={{ action: 'combo_send_form', shortId: journeyActionId }} />
        </Sidebar.Footer>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Header {...props}>
        <InfoButton>
          <Info />
        </InfoButton>
      </Header>
      <Sidebar.Content>
        <AnimatePresence initial={false} exitBeforeEnter>
          <Slide key="settings" offset={30}>
            <FormSettings
              template={journeyActionFormData}
              action={data}
              emailAddressOptions={emailAddressOptions}
            />
          </Slide>
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

export const Info = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('infoTitle')} />
      <Text
        font="regular14"
        text={styledTranslate({
          key: 'infoDescription',
          variables: {
            forms: {
              text: `"${t('forms')}"`,
              font: 'bold14',
            },
          },
          translations: locale,
        })}
      />
    </Stack>
  );
};

type JourneyActionFormProps = {
  template: Pick<JourneyActionForm, 'shortId' | 'name'>;
  action: JourneyActionDetailed<'combo_send_form'>;
  emailAddressOptions: EmailAddressOptions;
};

export const FormSettings = ({ template, action, emailAddressOptions }: JourneyActionFormProps) => {
  const formStore = useCreateActionFormStore();
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasContentAIEnabled } = useFeature('FEATURE_AI_CONTENT');
  const { isEnabled: hasSMSEnabled } = useFeature('FEATURE_SMS');
  const { invalidateJourneyAction } = useInvalidateJourneyActions();
  const formValues = formStore.useFormValues() as FormData;
  const hasEmailNotify = formStore.useFieldValue('emailNotify') as boolean;
  const hasSmsNotify = formStore.useFieldValue('smsNotify') as boolean;
  const [showNotifyError, setShowNotifyError] = useState(false);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [formLinkDraft, setFormLinkDraft] = useState<FormLinkDraft | null>(null);
  const notifyError = !hasEmailNotify && !hasSmsNotify;
  useEffect(() => {
    if (!notifyError && showNotifyError) {
      setShowNotifyError(false);
    }
  }, [notifyError, showNotifyError]);
  const isFormDirty = formStore.useIsDirty();
  const formDescription = formStore.useFieldValue('description');
  const hasNoSavedValues = !action.values;
  const isDirty = isFormDirty || hasNoSavedValues;
  const fieldMappingId = getFieldMappingId(action.journey);
  const fallbackDescription = !action.values ? template.name : '';

  const emailVariableGroups = useVariableGroups({
    actionVariables: action?.actionVariables,
    journeyId: action.journey,
    types: ['email'],
  });
  const smsVariableGroups = useVariableGroups({
    actionVariables: action?.actionVariables,
    journeyId: action.journey,
    types: ['phone'],
  });
  const formAction = action.groupActions?.find(
    (action) => (action.action as GROUP_ACTION_TYPES) === 'instantiate_form',
  );
  const allVariableGroups = useVariableGroups({
    actionVariables: action?.actionVariables,
    journeyId: action.journey,
    excludedTypes: ['file', 'file_multi'],
  });

  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journey,
    types: ['file', 'file_multi'],
  });
  const values = action.values;

  // SMS is plain text and never offers the link-type popover, so it omits the
  // linkType/linkLabel defaults; email defaults to Button/"Open form" so pills
  // inserted from the variable picker start out with the new default.
  const createFormLinkVariableGroups = useCallback(
    (hasLinkType: boolean): FieldMappingGroup[] => {
      // An action whose group actions are missing instantiate_form (seen in
      // half-deleted data) has no form link to offer — don't crash the sidebar.
      if (!formAction) return allVariableGroups;
      return [
        ...allVariableGroups,
        {
          action: 'combo_send_form',
          id: formAction.shortId,
          icon: <IconForm color={getActionIconColor(ACTION_TYPE)} />,
          label: t('form'),
          fields: [
            {
              action: formAction.shortId,
              label: t('formLink'),
              source: 'action',
              field: FORM_LINK_FIELD,
              type: 'text',
              ...(hasLinkType && { linkType: 'button', linkLabel: DEFAULT_LINK_LABEL }),
            },
          ],
        },
      ];
    },
    [allVariableGroups, formAction, t],
  );

  const smsMessageVariableGroups: FieldMappingGroup[] = useMemo(
    () => createFormLinkVariableGroups(false),
    [createFormLinkVariableGroups],
  );

  const emailMessageVariableGroups: FieldMappingGroup[] = useMemo(
    () => createFormLinkVariableGroups(true),
    [createFormLinkVariableGroups],
  );

  const tVariable = useVariableLocale();
  const editMutation = useUpdateJourneyAction(action.shortId);
  const uploadMutation = useUploadFile(
    createJourneyActionDocumentPath(action?.journey ?? '', action.shortId),
    true,
  );
  const sendEmailGroupAction = action.groupActions?.find(
    (groupAction) => groupAction.action === 'send_email',
  );
  const sendSmsGroupAction = action.groupActions?.find(
    (groupAction) => groupAction.action === 'send_sms',
  );

  const emailPreview: PreviewEmailProps | undefined = sendEmailGroupAction && {
    action: 'journey',
    shortId: sendEmailGroupAction.shortId,
    to: recipientTokens(formValues.emailRecipient),
    subject: formValues.emailSubject?.value
      ? replaceVariableTokensWithLabels(
          formValues.emailSubject.value,
          formValues.emailSubject.variables,
        )
      : undefined,
    attachments: getPreviewAttachments(formValues.attachments),
    sendFromEmailId: formValues.sendFromEmailId,
    // emailMessage is the editor body (mistyped as string on FormData). Overlay
    // the popover's uncommitted draft so the live preview reflects link-type
    // edits before they're committed via updateAttributes.
    liveContent: applyFormLinkDraft(
      formValues.emailMessage as unknown as FieldMappingEditorValue,
      formLinkDraft,
    ),
    brokenLabels: collectBrokenVariableLabels([formValues.emailRecipient], formValues.emailSubject),
  };

  const smsPreview: PreviewSmsProps | undefined = sendSmsGroupAction && {
    action: 'journey',
    shortId: sendSmsGroupAction.shortId,
    to: recipientTokens(formValues.smsRecipient),
    textMessage: formValues.smsMessage?.value ?? '',
    liveContent: formValues.smsMessage,
    brokenLabels: collectBrokenVariableLabels([formValues.smsRecipient], undefined),
  };

  const handleSave = useEvent(async (data: FormData) => {
    if (notifyError) {
      setShowNotifyError(true);
      return false;
    }
    if (emailLinkMissing || smsLinkMissing) {
      return false;
    }
    const hasReminder = data.emailReminderSwitch || data.smsReminderSwitch;
    const submissionData = {
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: 'combo_send_form',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        instantiateForm: {
          templateId: {
            value: template.shortId,
          },
        },
        emailNotify: data.emailNotify,
        smsNotify: data.smsNotify,
        ...(data.emailNotify && {
          sendEmail: {
            sendFromEmailId: data.sendFromEmailId,
            recipients: data.emailRecipient,
            subject: data.emailSubject,
            content: data.emailMessage,
            attachments: data.attachments,
          },
        }),
        ...(data.smsNotify && {
          sendSms: {
            recipients: data.smsRecipient,
            message: data.smsMessage,
          },
        }),
      },
      ...(hasReminder && {
        reminder: {
          journeyAction: action.shortId,
          ...(data.emailReminderSwitch && {
            email_data: {
              recipients: data.emailRecipient,
              content: data.emailMessage,
              subject: {
                value: `[Reminder] ${data.emailSubject?.value}`,
                variables: data.emailSubject?.variables,
              },
            },
          }),
          ...(data.smsReminderSwitch && {
            sms_data: {
              recipients: data.smsRecipient,
              message: data.smsMessage,
            },
          }),
          sms_notify: data.smsReminderSwitch,
          email_notify: data.emailReminderSwitch,
          hours: data.hour,
          minutes: data.minute,
          frequency: String(data.frequency),
          amount: String(data.amount),
          allowed_weekdays: data.allowedWeekdays,
        },
      }),
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    };
    await editMutation.mutateAsync(submissionData);
    invalidateJourneyAction(action.shortId);
  });

  const defaultEmailSubjectValue: FieldMappingEditorValue = {
    value: '{{ company_name }} has sent you a form',
    variables: {
      company_name: {
        source: 'company',
        field: 'name',
        label: t('companyName'),
        type: 'text',
      },
    },
  };

  const defaultEmailMessageValue: FieldMappingEditorValue = {
    value:
      '<p>Dear {{ company_traveller_name }}, <br/>You have just received a new form that requires your input.<br/><br/>Please find the form here:<br/>{{ action_unique_link }}</br></br>We kindly request you to fill this out at your earliest convenience.</p>',
    variables: {
      company_traveller_name: {
        source: 'company_traveller',
        field: 'name',
        label: t('travellerName'),
        type: 'text',
      },
      action_unique_link: {
        source: 'action',
        action: formAction?.shortId,
        field: FORM_LINK_FIELD,
        label: t('formLink'),
        type: 'text',
        linkType: 'button',
        linkLabel: DEFAULT_LINK_LABEL,
      },
    },
  };

  const defaultSMSMessageValue: FieldMappingEditorValue = {
    value:
      'Dear {{ company_traveller_name }}, \nYou have just received a new form that requires your input.\n\nPlease find the form here:\n{{ action_unique_link }}.\n\nWe kindly request you to fill this out at your earliest convenience.',
    variables: {
      company_traveller_name: {
        source: 'company_traveller',
        field: 'name',
        label: t('travellerName'),
        type: 'text',
      },
      action_unique_link: {
        source: 'action',
        action: formAction?.shortId,
        field: FORM_LINK_FIELD,
        label: t('formLink'),
        type: 'text',
      },
    },
  };

  const actionDescription = formDescription || action.description;

  const emailEditorRef = useRef<EditorMethods>(null);
  const emailMessage = formStore.useFieldValue('emailMessage') as FieldMappingEditorValue;
  const emailValue = emailMessage?.value ?? '';
  const emailLinkMissing =
    formValues.emailNotify && !hasFieldMappedFormLink(emailValue, formAction?.shortId);

  const smsEditorRef = useRef<EditorMethods>(null);
  const smsMessage = formStore.useFieldValue('smsMessage') as FieldMappingEditorValue;
  const smsValue = smsMessage?.value ?? '';
  const smsLinkMissing =
    formValues.smsNotify && !hasFieldMappedFormLink(smsValue, formAction?.shortId);

  const emailAiOptions = hasContentAIEnabled
    ? {
        generateChoices: actionDescription
          ? [
              {
                title: t('generateFromActionDescription'),
                prompt: `Write an email based on this description: ${actionDescription}`,
              },
            ]
          : [],
        threadInstructions:
          'The user is creating email content. You should only write the content not the email subject. Keep the response concise and formatted correctly.',
        useWebSocket,
      }
    : undefined;

  const smsAiOptions = hasContentAIEnabled
    ? {
        generateChoices: actionDescription
          ? [
              {
                title: t('generateFromActionDescription'),
                prompt: `Write a sms message for this description: ${actionDescription}`,
              },
            ]
          : [],
        threadInstructions:
          'The user is creating a sms message. Keep the response concise and formatted correctly, and do not include any tags such as <b> or any HTML formatting—just plain text for SMS.',
        useWebSocket,
      }
    : undefined;

  return (
    <FormWithUnsavedChangesPrompt
      overrideErrors={editMutation.error}
      store={formStore}
      onSubmit={handleSave}
    >
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Stack space={4}>
            <DescriptionFormField defaultValue={action.description || fallbackDescription} />
            <TemplateCardForm
              formId={template.shortId}
              name={template.name}
              isPreviewOnly={isInTemplateRoute()}
              actionVariables={action.actionVariables}
            />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
            hideLabel
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('notificationMethod')}>
          <Stack space={3}>
            {showNotifyError && notifyError && <FormError text={t('notifyError')} />}
            <Stack space={4} orientation="horizontal" align="center" justify="space-between">
              <Stack orientation="horizontal" space={2} align="center">
                <IconEmail color="elementPurple50" />
                <Text font="semibold14" text={t('email')} />
              </Stack>
              <Form.Switch
                defaultValue={values?.emailNotify ?? true}
                id="emailNotify"
                label={t('email')}
                labelVisible={false}
                onChange={(value) => {
                  if (!value) {
                    formStore.actions.setValue('emailReminderSwitch', false);
                  }
                }}
              />
            </Stack>
            <Form.ConditionalValue id="emailNotify" when={(value) => Boolean(value)} animate>
              <Stack space={4}>
                <SendFromEmailSelect
                  Form={Form}
                  emailAddressOptions={emailAddressOptions}
                  defaultValue={values?.sendEmail?.sendFromEmailId}
                />
                <Form.EmailListField
                  label={t('recipient')}
                  id="emailRecipient"
                  variableGroups={emailVariableGroups}
                  info={t('recipientInfo')}
                  validators={['required']}
                  fieldMappingId={fieldMappingId}
                  defaultValue={
                    values?.sendEmail?.recipients ?? [
                      {
                        value: '{{ traveller_email }}',
                        variables: {
                          traveller_email: {
                            source: 'traveller',
                            field: 'email',
                            label: tVariable('core.email'),
                          },
                        },
                      },
                    ]
                  }
                />
                <Form.FieldMappingTextEditor
                  id="emailSubject"
                  label={t('emailSubject')}
                  plaintext
                  singleLine
                  variableGroups={allVariableGroups}
                  defaultValue={values?.sendEmail?.subject ?? defaultEmailSubjectValue}
                  validators={['required']}
                  fieldMappingId={fieldMappingId}
                />
                <Stack space={1}>
                  <Form.FieldMappingTextEditor
                    label={t('messageBody')}
                    id="emailMessage"
                    variableGroups={emailMessageVariableGroups}
                    validators={['required']}
                    ref={emailEditorRef}
                    defaultValue={values?.sendEmail?.content ?? defaultEmailMessageValue}
                    fieldMappingId={fieldMappingId}
                    aiOptions={emailAiOptions}
                    formLinkType={{
                      isEnabled: true,
                      // AC6: opening the link configuration always opens the
                      // email preview.
                      onConfigOpen: () => setIsEmailPreviewOpen(true),
                      onDraftChange: setFormLinkDraft,
                    }}
                  />
                  {emailLinkMissing && (
                    <FormLinkMissing
                      editorRef={emailEditorRef}
                      formShortId={formAction?.shortId}
                      variableGroups={emailMessageVariableGroups}
                      hasLinkType
                    />
                  )}
                </Stack>
                <Form.FieldMappingFileListField
                  id="attachments"
                  label={t('emailAttachment')}
                  defaultValue={values?.sendEmail?.attachments}
                  variableGroups={fileVariableGroups}
                  onSelect={uploadMutation.mutateAsync}
                  fieldMappingId={fieldMappingId}
                />
              </Stack>
              <Separator style={{ margin: `${pxToRem(16)} 0 ${pxToRem(4)} 0` }} />
            </Form.ConditionalValue>
            {hasSMSEnabled && (
              <>
                <Stack space={4} orientation="horizontal" align="center" justify="space-between">
                  <Stack orientation="horizontal" space={2} align="center">
                    <IconSms color="elementPurple50" />
                    <Text font="semibold14" text={t('sms')} />
                  </Stack>
                  <Form.Switch
                    id="smsNotify"
                    label={t('sms')}
                    labelVisible={false}
                    defaultValue={Boolean(values?.smsNotify)}
                    onChange={(value) => {
                      if (!value) {
                        formStore.actions.setValue('smsReminderSwitch', false);
                      }
                    }}
                  />
                </Stack>
                <Form.ConditionalValue id="smsNotify" when={(value) => Boolean(value)} animate>
                  <Stack space={4}>
                    <Form.FieldMappingPhoneListField
                      label={t('recipient')}
                      id="smsRecipient"
                      variableGroups={smsVariableGroups}
                      validators={['required']}
                      fieldMappingId={fieldMappingId}
                      defaultValue={
                        values?.sendSms?.recipients ?? [
                          {
                            value: '{{ traveller_journey_phone }}',
                            variables: {
                              traveller_journey_phone: {
                                source: 'traveller_journey',
                                field: 'phone',
                                label: tVariable('core.phone'),
                              },
                            },
                          },
                        ]
                      }
                    />
                    <Stack space={1}>
                      <Form.FieldMappingTextEditor
                        plaintext
                        label={t('message')}
                        id="smsMessage"
                        ref={smsEditorRef}
                        variableGroups={smsMessageVariableGroups}
                        validators={['required']}
                        defaultValue={values?.sendSms?.message ?? defaultSMSMessageValue}
                        fieldMappingId={fieldMappingId}
                        aiOptions={smsAiOptions}
                      />
                      {smsLinkMissing && (
                        <FormLinkMissing
                          editorRef={smsEditorRef}
                          formShortId={formAction?.shortId}
                          variableGroups={smsMessageVariableGroups}
                        />
                      )}
                    </Stack>
                    <SMSMessageCountWarning message={formValues?.smsMessage} />
                  </Stack>
                </Form.ConditionalValue>
              </>
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer
          title={t('reminder')}
          defaultClosed
          counter={
            formValues.emailReminderSwitch || formValues.smsReminderSwitch
              ? String(formValues.amount ?? 0)
              : '0'
          }
          dataTestid="journey-sidebar-reminders-section"
        >
          <EditRemindersContent action={action} formStore={formStore} />
        </HideContainer>
        <Sidebar.Separator />
        <TimeSavedContainer {...action.timeSaved} />
      </Stack>
      <ActionFooter
        action={action}
        isDisabled={!isDirty || notifyError || emailLinkMissing || smsLinkMissing}
        form={Form}
      >
        {(hasEmailNotify || hasSmsNotify) && (
          <Menu
            trigger={IconButton}
            triggerProps={{
              variant: 'ghost-purple',
              icon: <IconOpenPreview />,
            }}
            dataTestid="send-form-preview-menu"
            placement="top-start"
          >
            {hasEmailNotify && (
              <Menu.Item
                label={t('previewEmail')}
                icon={<IconOpenPreview />}
                isDisabled={!emailPreview}
                dataTestId="send-form-preview-email-item"
                onSelect={() => setIsEmailPreviewOpen(true)}
              />
            )}
            {hasSmsNotify && <PreviewSmsMenuItemView previewProps={smsPreview} />}
          </Menu>
        )}
      </ActionFooter>
      {isEmailPreviewOpen && hasEmailNotify && emailPreview && (
        <EmailPreviewPanel {...emailPreview} onClose={() => setIsEmailPreviewOpen(false)} />
      )}
    </FormWithUnsavedChangesPrompt>
  );
};

export const hasFieldMappedFormLink = (text: string, formActionShortId?: string): boolean => {
  const formLinks = [
    '{{ action_unique_link }}',
    formActionShortId ? `{{ ${formActionShortId}_unique_link }}` : '',
  ].filter(Boolean) as string[];
  return formLinks.some((link) => text.includes(link));
};

export const FormLinkMissing = ({
  editorRef,
  formShortId,
  variableGroups,
  hasLinkType = false,
}: {
  editorRef: RefObject<EditorMethods>;
  formShortId?: string;
  variableGroups: FieldMappingGroup[];
  hasLinkType?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const field = {
    source: 'action',
    action: formShortId ?? '',
    field: FORM_LINK_FIELD,
    label: t('formLink'),
    type: 'text',
    ...(hasLinkType && { linkType: 'button', linkLabel: DEFAULT_LINK_LABEL }),
  };
  const variableLabel = getVariableLabel(field as FieldMappingFieldItem, variableGroups);
  const variableName = extractName(field as MappableVariable);
  const placeholder = getPlaceholderHtml({
    ...field,
    variable: variableName,
    label: variableLabel,
  });
  return (
    <Stack space={4}>
      <Stack space={2} orientation="horizontal" align="top">
        <IconFormIndicatorError color="elementRed100" />
        <Text font="regular14" color="elementRed100" text={t('formLinkRemoved')} />
      </Stack>
      <Stack orientation="horizontal">
        <Button
          text={t('addFormLink')}
          iconLeft={<IconTextFormatLink />}
          variant="primary-blue"
          onClick={() => {
            editorRef.current?.insertContent(placeholder);
          }}
        />
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    ...previewEmailLocale.en,
    form: 'Form',
    formLink: 'Form Link',

    addAddress: 'Add email address',
    message: 'Message',
    description: 'Description',
    sendFrom: 'Send from',
    recipient: 'Send to',
    replaceTemplate: 'Replace form template',
    noForms: 'You do not have any forms',
    noFormsSearch: 'You do not have any form templates that match your current search',
    emptyStateText: 'Please create a form in the form builder and come back',
    recipientInfo: 'E-mail address of the person that will receive the form',
    travellerName: 'Traveller name',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    notificationMethod: 'Notification method',
    reminder: 'Reminders',
    email: 'Email',
    sms: 'SMS',
    messageBody: 'Message (body)',
    emailAttachment: 'Email attachment',
    repeat: 'Repeat reminders',
    frequency: 'Reminder Frequency',
    daysInParentheses: '(Days)',
    emailSubject: 'Email subject',
    companyName: 'Company name',
    infoTitle: 'About Send forms',
    forms: 'Forms',
    infoDescription:
      "Allows you to send pre-created forms to one person. When the person has completed the form the action is marked as 'completed' and the journey will continue.\n\nYou can use information received through submitted forms in other actions through the field-mapping options.\n\nWe recommend previewing the form before publishing the journey workflow.",
    createForm: 'Create form',
    newForm: 'New Form',
    timezoneTooltip: 'Action • {action}\nYour computer • {computer}',
    generateFromActionDescription: 'Generate from action description',
    formLinkRemoved: 'Form link missing, please re-add the form link to the message',
    addFormLink: 'Add form link',
    errorTitle: 'Error loading action',
    deprecatedHeader: 'This action is deprecated',
    deprecatedAlert:
      '\nThis “Send Form” action comes from an older workflow version and is no longer supported. Please replace it with a new version of the send form action.',
    formMissingTitle: 'Form not found',
    notifyError: 'At least one notification method is required',
  },
};
